import { supabase } from './supabase';

/**
 * StoryEntity interface
 */
export interface StoryEntity {
  id: number;
  story_id: string;
  entity_id: number;
  role: string;
  created_at: string;
  updated_at: string;
}

/**
 * StoryEntityWithDetails interface
 */
export interface StoryEntityWithDetails extends StoryEntity {
  entity: {
    id: number;
    name: string;
    description: string;
    status: string;
    template_id: number;
    templates?: {
      name: string;
    };
  };
}

/**
 * Get entities for a story
 * @param storyId - Story ID
 * @returns Promise with array of story entities
 */
export const getStoryEntities = async (storyId: string): Promise<StoryEntityWithDetails[]> => {
  const { data, error } = await supabase
    .from('story_entities')
    .select(`
      *,
      entity:entity_id (
        *,
        templates:template_id (
          name
        )
      )
    `)
    .eq('story_id', storyId)
    .order('created_at');

  if (error) {
    console.error('Error fetching story entities:', error);
    throw error;
  }

  return data || [];
};

/**
 * Add an entity to a story
 * @param storyId - Story ID
 * @param entityId - Entity ID
 * @param role - Role of the entity in the story
 * @returns Promise with created story entity
 */
export const addEntityToStory = async (
  storyId: string,
  entityId: number,
  role: string = ''
): Promise<StoryEntity> => {
  const { data, error } = await supabase
    .from('story_entities')
    .insert([
      {
        story_id: storyId,
        entity_id: entityId,
        role
      }
    ])
    .select()
    .single();

  if (error) {
    console.error('Error adding entity to story:', error);
    throw error;
  }

  return data;
};

/**
 * Remove an entity from a story
 * @param storyId - Story ID
 * @param entityId - Entity ID
 * @returns Promise with success status
 */
export const removeEntityFromStory = async (
  storyId: string,
  entityId: number
): Promise<void> => {
  const { error } = await supabase
    .from('story_entities')
    .delete()
    .eq('story_id', storyId)
    .eq('entity_id', entityId);

  if (error) {
    console.error('Error removing entity from story:', error);
    throw error;
  }
};

/**
 * Update an entity's role in a story
 * @param storyId - Story ID
 * @param entityId - Entity ID
 * @param role - New role
 * @returns Promise with updated story entity
 */
export const updateEntityRole = async (
  storyId: string,
  entityId: number,
  role: string
): Promise<StoryEntity> => {
  const { data, error } = await supabase
    .from('story_entities')
    .update({ role })
    .eq('story_id', storyId)
    .eq('entity_id', entityId)
    .select()
    .single();

  if (error) {
    console.error('Error updating entity role:', error);
    throw error;
  }

  return data;
};
